import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { SERVICE_AREAS, BUSINESS } from '../../mocks/sharedData';

interface ServiceAreaMapProps {
  eyebrow?: string;
  headline?: string;
  highlightCity?: string;
}

const CITY_SERVICES = [
  { city: 'Jeffersonville, IN', service: 'Residential & Commercial Solar' },
  { city: 'New Albany, IN', service: 'Home Solar Installation' },
  { city: 'Clarksville, IN', service: 'Solar Panel Installation' },
  { city: 'Sellersburg, IN', service: 'Residential Solar' },
  { city: 'Scottsburg, IN', service: 'Home Solar Systems' },
  { city: 'Corydon, IN', service: 'Solar Installation' },
  { city: 'Seymour, IN', service: 'Commercial Solar' },
  { city: 'Columbus, IN', service: 'Solar Energy Systems' },
  { city: 'Louisville, KY', service: 'Residential & Commercial Solar' },
  { city: 'Shelbyville, KY', service: 'Home Solar Installation' },
  { city: 'Frankfort, KY', service: 'Solar Panel Systems' },
  { city: 'Georgetown, KY', service: 'Residential Solar' },
  { city: 'Elizabethtown, KY', service: 'Solar Installation' },
  { city: 'Bardstown, KY', service: 'Home Solar Systems' },
  { city: 'La Grange, KY', service: 'Solar Energy' },
  { city: 'Shepherdsville, KY', service: 'Residential Solar' },
];

const ServiceAreaMap = ({
  eyebrow = 'Where We Work',
  headline = 'Serving All of Kentuckiana',
  highlightCity,
}: ServiceAreaMapProps) => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="bg-zinc-950 py-32" id="locations">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          ref={ref}
          className={`max-w-2xl mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-px bg-amber-500" />
            <span className="text-amber-400 text-xs font-semibold tracking-[0.25em] uppercase">{eyebrow}</span>
          </div>
          <h2 className="font-serif text-5xl md:text-6xl text-white leading-tight mb-5">{headline}</h2>
          <p className="text-zinc-400 text-lg leading-relaxed">
            One trusted solar team. Two states. Southern Indiana and North Central Kentucky —
            we know the permitting, the utilities, and the climate better than anyone.
          </p>
        </div>

        {/* Map — full width, tall */}
        <div
          className={`rounded-3xl overflow-hidden border border-zinc-800 mb-8 transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ height: '520px' }}
        >
          <iframe
            title="Star Solar Specialists — 509 Ohio Ave, Jeffersonville IN"
            src="https://maps.google.com/maps?q=509+Ohio+Ave,+Jeffersonville,+IN+47130&t=m&z=10&output=embed"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
          />
        </div>

        {/* Contact + Cities row */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-3 gap-6 transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Contact card */}
          <div className="bg-zinc-900 rounded-3xl p-8 border border-zinc-800">
            <h3 className="text-white font-serif text-xl font-semibold mb-6">Our Office</h3>
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-500/15 rounded-xl flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-fill text-amber-400" />
                </div>
                <div>
                  <p className="text-zinc-300 leading-snug">{BUSINESS.fullAddress}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-500/15 rounded-xl flex-shrink-0">
                  <i className="ri-phone-fill text-amber-400" />
                </div>
                <a href={BUSINESS.phoneTel} className="text-zinc-300 hover:text-amber-400 transition-colors cursor-pointer font-semibold">
                  {BUSINESS.phone}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-500/15 rounded-xl flex-shrink-0">
                  <i className="ri-mail-fill text-amber-400" />
                </div>
                <a href={`mailto:${BUSINESS.email}`} className="text-zinc-300 hover:text-amber-400 transition-colors cursor-pointer text-xs">
                  {BUSINESS.email}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-500/15 rounded-xl flex-shrink-0">
                  <i className="ri-time-fill text-amber-400" />
                </div>
                <span className="text-zinc-300">Mon–Fri &nbsp;9:00 AM – 5:00 PM</span>
              </div>
            </div>
            <a
              href={BUSINESS.consultationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 block w-full text-center bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold py-4 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors"
            >
              Get Free Estimate
            </a>
          </div>

          {/* Cities — keyword-rich grid */}
          <div className="lg:col-span-2 bg-zinc-900 rounded-3xl p-8 border border-zinc-800">
            <h3 className="text-white font-serif text-xl font-semibold mb-6">Service Areas &amp; Locations</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
              {CITY_SERVICES.map(({ city, service }) => (
                <a
                  key={city}
                  href="/locations"
                  className={`group flex items-start gap-3 cursor-pointer ${
                    city === highlightCity ? 'opacity-100' : 'opacity-80 hover:opacity-100'
                  } transition-opacity`}
                >
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className={`ri-map-pin-2-fill text-sm ${
                      city === highlightCity ? 'text-amber-400' : 'text-amber-600/60 group-hover:text-amber-400'
                    } transition-colors`} />
                  </div>
                  <div>
                    <p className={`text-sm font-semibold leading-none mb-0.5 ${
                      city === highlightCity ? 'text-amber-400' : 'text-zinc-300 group-hover:text-white'
                    } transition-colors`}>
                      {city}
                    </p>
                    <p className="text-zinc-500 text-xs">{service}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreaMap;
